import {
  assertInInjectionContext,
  inject,
  Injector,
  Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  DeclarativeForm,
  FormDeclaration,
} from '@fmnts/declarative-forms/core';
import * as Fn from 'effect/Function';
import { DeclarativeFormComponent } from './components/declarative-form.component';

interface UtilOptions {
  readonly injector?: Injector;
}

/**
 * Exposes the current declarative form.
 *
 * `getDeclarativeForm` must be called in an injection context unless an injector is provided via options.
 */
export function getDeclarativeForm(options: UtilOptions): DeclarativeForm {
  if (!options?.injector) {
    assertInInjectionContext(getDeclarativeForm);
  }
  return _getRootComponentFromInjector(options).form;
}

/**
 * Exposes the current form declaration.
 *
 * `getFormDeclaration` must be called in an injection context unless an injector is provided via options.
 */
export function getFormDeclaration(
  options: UtilOptions,
): Signal<FormDeclaration> {
  if (!options?.injector) {
    assertInInjectionContext(getFormDeclaration);
  }
  return Fn.pipe(_getRootComponentFromInjector(options), (component) =>
    toSignal(component.declaration$, { ...options, requireSync: true }),
  );
}

/**
 * Provides access to the root component from the given injector or
 * the current injection context.
 */
function _getRootComponentFromInjector(options: UtilOptions) {
  const injector = options?.injector ?? inject(Injector);
  return injector.get(DeclarativeFormComponent);
}
